/**
 * Reports-Tab der Anwesenheits-Seite (G-16): Zeitraum-Report mit Status-/
 * Schicht-/Stellen-Filter (Multi-Select als Chips), KPI-Karten (Σ Stunden,
 * Σ Zulagen, Buchungen, offen), XLSX-Export (Blob, Header-Auth) und
 * Admin-Korrektur-Dialog (adminUpdate, Gate personal_schreiben).
 * Darunter der Zulagen-Monatsreport (Summen je Zulagenart + je Mitarbeiter).
 */

import { useMemo, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BadgeEuro, CheckCheck, Clock, Download, ListChecks, Pencil } from "lucide-react"
import { toast } from "sonner"
import { EntityFormSheet, type FieldDef, type FormValues } from "@/components/admin/EntityFormSheet"
import { ATTENDANCE_STATUS, ATTENDANCE_STATUS_VALUES } from "@/components/attendance/labels"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { fmtDate, fmtEuro, fmtHours } from "@/lib/format"
import { todayIso } from "@/lib/isoWeek"
import { useCan } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type { Anwesenheitsstatus, AttendanceReportRow } from "@/types"

/** Multi-Select als Chip-Leiste (Schichten/Stellen sind überschaubar) */
function MultiChips({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: { id: string; name: string }[]
  selected: Set<string>
  onToggle: (id: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex max-w-xl flex-wrap gap-1">
        {options.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              selected.has(o.id)
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-transparent text-muted-foreground hover:bg-accent"
            )}
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  )
}

function KpiCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock
  label: string
  value: string
}) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Icon className="size-4" /> {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
]

export function AttendanceReportsTab() {
  const canWrite = useCan("personal_schreiben")
  const queryClient = useQueryClient()
  const today = useMemo(todayIso, [])
  const monthStart = today.slice(0, 8) + "01"

  // --- Zeitraum-Report -----------------------------------------------------
  const [from, setFrom] = useState(monthStart)
  const [to, setTo] = useState(today)
  const [status, setStatus] = useState<Anwesenheitsstatus | "">("")
  const [shiftSel, setShiftSel] = useState<Set<string>>(new Set())
  const [positionSel, setPositionSel] = useState<Set<string>>(new Set())
  const [openOnly, setOpenOnly] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [correction, setCorrection] = useState<AttendanceReportRow | null>(null)

  const shifts = useQuery({
    queryKey: ["shifts"],
    queryFn: () => api.organization.shifts.list({ size: 100 }),
  })
  const positions = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.organization.positions.list({ size: 100 }),
  })

  const params = {
    size: 100,
    from: from || undefined,
    to: to || undefined,
    status: status || undefined,
    shiftIds: shiftSel.size > 0 ? [...shiftSel].join(",") : undefined,
    positionIds: positionSel.size > 0 ? [...positionSel].join(",") : undefined,
    openOnly: openOnly || undefined,
  }
  const report = useQuery({
    queryKey: ["reports", "attendance", params],
    queryFn: () => api.reports.attendance(params),
    placeholderData: keepPreviousData,
  })

  function toggle(set: Set<string>, setter: (s: Set<string>) => void, id: string) {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setter(next)
  }

  async function downloadXlsx() {
    setDownloading(true)
    try {
      await api.reports.downloadAttendanceXlsx({ ...params, size: undefined, page: undefined })
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setDownloading(false)
    }
  }

  const adminUpdate = useMutation({
    mutationFn: ({ id, values }: { id: string; values: FormValues }) =>
      api.attendance.adminUpdate(id, {
        hours: values.hours ? Number(values.hours) : null,
        status: (values.status as Anwesenheitsstatus) || null,
        info: String(values.info).trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports", "attendance"] })
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      toast.success("Buchung korrigiert (Zulagen neu eingefroren)")
      setCorrection(null)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const correctionFields: FieldDef[] = [
    { key: "hours", label: "Stunden (1–12)", kind: "number", step: "0.25" },
    {
      key: "status",
      label: "Status",
      kind: "select",
      options: ATTENDANCE_STATUS_VALUES.map((s) => ({
        value: s,
        label: ATTENDANCE_STATUS[s].label,
      })),
    },
    { key: "info", label: "Hinweis", kind: "text" },
  ]

  // --- Zulagen-Monatsreport --------------------------------------------------
  const currentYear = new Date().getFullYear()
  const [scYear, setScYear] = useState(String(currentYear))
  const [scMonth, setScMonth] = useState(String(new Date().getMonth() + 1))
  const [scEmployee, setScEmployee] = useState("")

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })

  const surchargeParams = {
    year: Number(scYear),
    month: scMonth ? Number(scMonth) : undefined,
    employeeId: scEmployee ? Number(scEmployee) : undefined,
  }
  const surcharges = useQuery({
    queryKey: ["reports", "surcharges", surchargeParams],
    queryFn: () => api.reports.surcharges(surchargeParams),
    placeholderData: keepPreviousData,
  })

  const rows = report.data?.data ?? []
  const totals = report.data?.totals

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<AttendanceReportRow>[]>(() => {
    const defs: ColumnDef<AttendanceReportRow>[] = [
      {
        id: "date",
        header: "Datum",
        // ISO-Datum sortiert chronologisch korrekt
        accessorFn: (r) => r.date,
        cell: ({ row }) => fmtDate(row.original.date),
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (r) => r.employeeName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "position",
        header: "Stelle",
        accessorFn: (r) => r.positionName ?? "",
        cell: ({ row }) => row.original.positionName ?? "—",
      },
      {
        id: "shift",
        header: "Schicht",
        accessorFn: (r) => r.shiftName ?? "",
        cell: ({ row }) => row.original.shiftName ?? "—",
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => ATTENDANCE_STATUS[r.status].label,
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <Badge variant="outline" className={ATTENDANCE_STATUS[row.original.status].badge}>
              {ATTENDANCE_STATUS[row.original.status].label}
            </Badge>
            {!row.original.checkedIn && (
              <span className="text-xs text-muted-foreground">ausgebucht</span>
            )}
          </span>
        ),
      },
      {
        id: "hours",
        header: "Stunden",
        accessorFn: (r) => (r.hours !== null ? Number(r.hours) : -1),
        cell: ({ row }) =>
          row.original.hours !== null ? fmtHours(Number(row.original.hours)) : "—",
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "surcharges",
        header: "Σ Zulagen",
        accessorFn: (r) => Number(r.surchargeTotal),
        cell: ({ row }) =>
          Number(row.original.surchargeTotal) > 0
            ? fmtEuro(Number(row.original.surchargeTotal))
            : "—",
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
    ]
    if (canWrite) {
      defs.push({
        id: "korrektur",
        header: "Korrektur",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "w-16 text-right", cellClassName: "text-right" },
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Admin-Korrektur"
            title="Admin-Korrektur (Stunden/Status/Hinweis)"
            onClick={() => setCorrection(row.original)}
          >
            <Pencil className="!size-3.5" />
          </Button>
        ),
      })
    }
    return defs
  }, [canWrite])

  return (
    <div className="space-y-4">
      {/* Filterleiste Zeitraum-Report */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Von</Label>
            <Input
              type="date"
              className="w-40"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Bis</Label>
            <Input
              type="date"
              className="w-40"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="w-44 space-y-1.5">
            <Label>Status</Label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as Anwesenheitsstatus | "")}
            >
              <option value="">Alle Status</option>
              {ATTENDANCE_STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {ATTENDANCE_STATUS[s].label}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex h-9 items-center gap-2">
            <Switch checked={openOnly} onCheckedChange={setOpenOnly} />
            <Label className="font-normal">Nur offene</Label>
          </div>
        </div>
        <Button variant="outline" onClick={downloadXlsx} disabled={downloading}>
          <Download /> XLSX-Export
        </Button>
      </div>

      <div className="flex flex-wrap gap-6">
        <MultiChips
          label="Schichten"
          options={(shifts.data?.data ?? []).map((s) => ({ id: s.id, name: s.name }))}
          selected={shiftSel}
          onToggle={(id) => toggle(shiftSel, setShiftSel, id)}
        />
        <MultiChips
          label="Stellen"
          options={(positions.data?.data ?? []).map((p) => ({ id: p.id, name: p.name }))}
          selected={positionSel}
          onToggle={(id) => toggle(positionSel, setPositionSel, id)}
        />
      </div>

      {/* KPI-Karten */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={Clock}
          label="Σ Stunden"
          value={totals ? fmtHours(Number(totals.totalHours)) : "—"}
        />
        <KpiCard
          icon={BadgeEuro}
          label="Σ Zulagen"
          value={totals ? fmtEuro(Number(totals.totalSurcharges)) : "—"}
        />
        <KpiCard
          icon={ListChecks}
          label="Buchungen"
          value={totals ? String(totals.recordCount) : "—"}
        />
        <KpiCard
          icon={CheckCheck}
          label="Davon offen"
          value={totals ? String(totals.openCount) : "—"}
        />
      </div>

      {/* Ergebnisgrid */}
      <Card>
        <CardContent className="py-2">
          {report.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="attendance-reports"
              emptyMessage="Keine Buchungen im gewählten Zeitraum."
            />
          )}
        </CardContent>
      </Card>

      {/* Zulagen-Monatsreport */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Zulagen-Monatsreport</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32 space-y-1.5">
              <Label>Jahr</Label>
              <Select value={scYear} onChange={(e) => setScYear(e.target.value)}>
                {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-40 space-y-1.5">
              <Label>Monat</Label>
              <Select value={scMonth} onChange={(e) => setScMonth(e.target.value)}>
                <option value="">Ganzes Jahr</option>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-56 space-y-1.5">
              <Label>Mitarbeiter</Label>
              <Select value={scEmployee} onChange={(e) => setScEmployee(e.target.value)}>
                <option value="">Alle Mitarbeiter</option>
                {(employees.data?.data ?? []).map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.displayName}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {surcharges.isLoading ? (
            <LoadingSpinner className="h-6 w-6" />
          ) : surcharges.data ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Summen je Zulagenart */}
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zulagenart</TableHead>
                      <TableHead className="text-right">Summe</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(surcharges.data.byType).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-muted-foreground">
                          Keine Zulagen im Zeitraum.
                        </TableCell>
                      </TableRow>
                    )}
                    {Object.entries(surcharges.data.byType).map(([type, amount]) => (
                      <TableRow key={type}>
                        <TableCell className="font-medium">{type}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtEuro(Number(amount))}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold">Gesamt</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {fmtEuro(Number(surcharges.data.total))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="mt-2 text-xs text-muted-foreground">
                  Σ Stunden: {fmtHours(Number(surcharges.data.totalHours))} · Live-Anteil
                  (offene Buchungen): {fmtEuro(Number(surcharges.data.livePortion))}
                </p>
              </div>

              {/* Summen je Mitarbeiter */}
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mitarbeiter</TableHead>
                      <TableHead className="text-right">Σ Zulagen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {surcharges.data.byEmployee.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-muted-foreground">
                          Keine Mitarbeiter-Zulagen im Zeitraum.
                        </TableCell>
                      </TableRow>
                    )}
                    {surcharges.data.byEmployee.map((row) => (
                      <TableRow key={row.employeeId}>
                        <TableCell className="font-medium">{row.employeeName}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtEuro(Number(row.total))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Keine Daten.</p>
          )}
        </CardContent>
      </Card>

      {/* Admin-Korrektur-Dialog */}
      <EntityFormSheet
        sheetKey="entity-form-attendance-reports"
        open={correction !== null}
        onOpenChange={(open) => {
          if (!open) setCorrection(null)
        }}
        title={
          correction
            ? `Korrektur — ${correction.employeeName}, ${fmtDate(correction.date)}`
            : "Korrektur"
        }
        description="Stunden/Status/Hinweis ändern — Zulagen werden neu berechnet und eingefroren, die Änderung landet im Journal."
        fields={correctionFields}
        initialValues={{
          hours: correction?.hours != null ? String(correction.hours) : "",
          status: correction?.status ?? "ANWESEND",
          info: "",
        }}
        pending={adminUpdate.isPending}
        onSubmit={(values) => {
          if (correction) adminUpdate.mutate({ id: correction.id, values })
        }}
      />
    </div>
  )
}
